import React, { useState, useRef, useEffect } from 'react';
import { Input } from '@components/atoms/Input';
import { Button } from '@components/atoms/Button';
import { Label } from '@components/atoms/Label';
import {
  FramingCalculatorInputs,
  FramingCalculatorResults,
  OnAddToMaterialCallback,
  OnAddAllMaterialsCallback,
  MaterialFromCalculator,
} from '../../types/calculator.types';
import { Plus } from 'lucide-react';
import { useTranslation } from '@hooks/useTranslation';

interface FramingCalculatorProps {
  onAddToMaterial?: OnAddToMaterialCallback;
  onAddAllMaterials?: OnAddAllMaterialsCallback;
}

const FramingCalculator: React.FC<FramingCalculatorProps> = ({
  onAddToMaterial,
  onAddAllMaterials,
}) => {
  const tCalc = useTranslation('calculators');
  const [inputs, setInputs] = useState<FramingCalculatorInputs>({
    wallLength: '',
    studSpacing: 16,
    numCorners: 2,
    numDoors: 0,
    numWindows: 0,
  });

  const [results, setResults] = useState<FramingCalculatorResults | null>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  // Scroll to results when they appear
  useEffect(() => {
    if (results && resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [results]);

  const handleInputChange =
    (field: keyof FramingCalculatorInputs) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setInputs((prev) => ({
        ...prev,
        [field]:
          field === 'studSpacing'
            ? Number(value)
            : field === 'numCorners' ||
                field === 'numDoors' ||
                field === 'numWindows'
              ? Number(value)
              : value,
      }));
    };

  const calculateFraming = () => {
    setResults(null);

    const parsedWallLength = parseFloat(inputs.wallLength);
    const parsedStudSpacing = parseFloat(inputs.studSpacing.toString());
    const parsedNumCorners = parseInt(inputs.numCorners.toString(), 10);
    const parsedNumDoors = parseInt(inputs.numDoors.toString(), 10);
    const parsedNumWindows = parseInt(inputs.numWindows.toString(), 10);

    if (isNaN(parsedWallLength) || parsedWallLength <= 0) {
      return;
    }
    if (isNaN(parsedStudSpacing) || parsedStudSpacing <= 0) {
      return;
    }
    if (
      isNaN(parsedNumCorners) ||
      parsedNumCorners < 0 ||
      isNaN(parsedNumDoors) ||
      parsedNumDoors < 0 ||
      isNaN(parsedNumWindows) ||
      parsedNumWindows < 0
    ) {
      return;
    }

    try {
      const wallLengthInInches = parsedWallLength * 12;

      let studsFromSpacing =
        Math.floor(wallLengthInInches / parsedStudSpacing) + 1;

      const extraStuds =
        parsedNumCorners * 2 + parsedNumDoors * 4 + parsedNumWindows * 4;

      const totalStuds = studsFromSpacing + extraStuds;

      const plateLength = parsedWallLength * 3;

      setResults({
        studsNeeded: totalStuds,
        platesNeeded: plateLength.toFixed(2),
      });
    } catch (error) {
      console.error('Calculation failed:', error);
    }
  };

  return (
    <div>
      <h3 className="text-xl font-semibold text-center text-gray-900 dark:text-white mb-6">
        {tCalc.names.framing}
      </h3>

      {/* Wall Information */}
      <div className="mb-6">
        <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-3">
          {tCalc.framing.wallInformation}
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label label={tCalc.framing.wallLengthInFeet} htmlFor="wallLength" />
            <Input
              id="wallLength"
              type="number"
              placeholder={`${tCalc.actions.examplePrefix}, 20`}
              value={inputs.wallLength}
              onChange={handleInputChange('wallLength')}
              className="mt-1"
            />
          </div>
          <div>
            <Label label={tCalc.framing.studSpacingIn} htmlFor="studSpacing" />
            <Input
              id="studSpacing"
              type="number"
              value={inputs.studSpacing}
              onChange={handleInputChange('studSpacing')}
              className="mt-1"
            />
          </div>
        </div>
      </div>

      {/* Openings & Corners */}
      <div className="mb-6">
        <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-3">
          {tCalc.framing.openingsAndCorners}
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <Label label={tCalc.counts.corners} htmlFor="numCorners" />
            <Input
              id="numCorners"
              type="number"
              value={inputs.numCorners}
              onChange={handleInputChange('numCorners')}
              className="mt-1"
            />
          </div>
          <div>
            <Label label={tCalc.counts.doors} htmlFor="numDoors" />
            <Input
              id="numDoors"
              type="number"
              value={inputs.numDoors}
              onChange={handleInputChange('numDoors')}
              className="mt-1"
            />
          </div>
          <div>
            <Label label={tCalc.counts.windows} htmlFor="numWindows" />
            <Input
              id="numWindows"
              type="number"
              value={inputs.numWindows}
              onChange={handleInputChange('numWindows')}
              className="mt-1"
            />
          </div>
        </div>
      </div>

      <Button
        fullWidth
        variant="primary"
        size="lg"
        onClick={calculateFraming}
        className="mb-6">
        {tCalc.framing.calculateButton}
      </Button>

      {results && (
        <div
          ref={resultsRef}
          className="p-4 mb-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border-t-4 border-indigo-600">
          <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-3">
            {tCalc.actions.results}
          </h4>
          <div className="space-y-3">
            {/* Total Studs Needed */}
            <div className="flex items-center justify-between">
              <p className="text-gray-700 dark:text-gray-300">
                <span className="font-semibold">{tCalc.framing.totalStudsNeeded}</span>{' '}
                <span className="text-indigo-600 dark:text-indigo-400 font-medium">
                  {results.studsNeeded}
                </span>{' '}
                {results.studsNeeded === 1 ? tCalc.framing.studsUnitOne : tCalc.framing.studsUnitOther}
              </p>
              {onAddToMaterial && (
                <Button
                  type="button"
                  variant="primary"
                  size="md"
                  leftIcon={<Plus className="w-4 h-4" />}
                  onClick={() =>
                    onAddToMaterial({
                      amount: results.studsNeeded,
                      amountType: 'units',
                      amountSystem: 'imperial',
                      suggestedName: tCalc.framing.wallStudsMaterial,
                      resultLabel: tCalc.framing.totalStudsNeeded,
                    })
                  }>
                  {tCalc.actions.addToMaterial}
                </Button>
              )}
            </div>

            {/* Total Plate Lumber */}
            <div className="flex items-center justify-between">
              <p className="text-gray-700 dark:text-gray-300">
                <span className="font-semibold">{tCalc.framing.totalPlateLumber}</span>{' '}
                <span className="text-indigo-600 dark:text-indigo-400 font-medium">
                  {results.platesNeeded}
                </span>{' '}
                {tCalc.framing.plateLumberUnit}
              </p>
              {onAddToMaterial && (
                <Button
                  type="button"
                  variant="primary"
                  size="md"
                  leftIcon={<Plus className="w-4 h-4" />}
                  onClick={() =>
                    onAddToMaterial({
                      amount: parseFloat(results.platesNeeded),
                      amountType: 'linearFeet',
                      amountSystem: 'imperial',
                      suggestedName: tCalc.framing.plateLumberMaterial,
                      resultLabel: tCalc.framing.totalPlateLumber,
                    })
                  }>
                  {tCalc.actions.addToMaterial}
                </Button>
              )}
            </div>

            {/* Add All Materials Button */}
            {onAddAllMaterials && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <Button
                  type="button"
                  variant="primary"
                  size="lg"
                  fullWidth
                  leftIcon={<Plus className="w-4 h-4" />}
                  onClick={() => {
                    const allMaterials: MaterialFromCalculator[] = [
                      {
                        amount: results.studsNeeded,
                        amountType: 'units',
                        amountSystem: 'imperial',
                        suggestedName: tCalc.framing.wallStudsMaterial,
                        resultLabel: tCalc.framing.totalStudsNeeded,
                      },
                      {
                        amount: parseFloat(results.platesNeeded),
                        amountType: 'linearFeet',
                        amountSystem: 'imperial',
                        suggestedName: tCalc.framing.plateLumberMaterial,
                        resultLabel: tCalc.framing.totalPlateLumber,
                      },
                    ];
                    onAddAllMaterials(allMaterials);
                  }}>
                  {tCalc.actions.addAllMaterials} (2)
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FramingCalculator;
